import { createSelector } from 'reselect';
import moment from 'moment';

import makeSelectMain, {
  getProfileCurrentSetting,
  selectChatbotWindowStatus,
  selectDocUploadStatusNotifications,
  selectHasUnreadDocUploadStatusNotifications,
  selectIsNYCLanguagePreferenceModalRequired,
} from '../Main/selectors';
import makeSelectLossDraft, {
  selectHasUnreadLossDraftClaimEvents,
  selectHasUnreadLossDraftMessages,
  selectLossDraftMessageInquiryNotifications,
  selectRequestStatus as selectLossDraftRequestStatus,
  selectLossDraftUnreadMessageNotifications,
} from '../LossDraft/selectors';
import {
  selectHasUnreadSecureMessages,
  selectSecureMessagingInquiryNotifications,
  selectRequestStatus as selectSecureMessagingRequestStatus,
  selectSecureMessagingUnreadMessageNotifications,
} from '../SecureMessaging/selectors';
import { formatIntlNotifications } from './helpers';
import { initialState } from './reducer';
import { RELEASE_NOTIFICATIONS } from './constants';

/**
 * Direct selector to the notifications state domain
 */
const selectNotificationsDomain = (state) => state.notifications || initialState;

/**
 * Direct selector to the intl object
 */
const selectIntl = (_, props) => props.intl;

// Selector located in Notifications selectors file vs Main to avoid circular dependency
const selectChatbotModalInteractionProps = () => createSelector(
  makeSelectMain('isAccountModalOpen'),
  makeSelectMain('isInactivityModalOpen'),
  makeSelectMain('isSwitchLoanModalOpen'),
  makeSelectNotifications('isDesktopNotificationsModalOpen'),
  (
    isAccountModalOpen,
    isInactivityModalOpen,
    isSwitchLoanModalOpen,
    isNotificationsModalOpen,
  ) => {
    let currentOpenModal = '';

    if (isAccountModalOpen) {
      currentOpenModal = 'isAccountModalOpen';
    } else if (isSwitchLoanModalOpen) {
      currentOpenModal = 'isSwitchLoanModalOpen';
    } else if (isInactivityModalOpen) {
      currentOpenModal = 'isInactivityModalOpen';
    } else if (isNotificationsModalOpen) {
      currentOpenModal = 'isNotificationsModalOpen';
    }

    return { currentOpenModal, isAnyModalOpen: !!currentOpenModal };
  },
);

const selectClientBanner = () => createSelector(
  makeSelectNotifications('postAuthNotifications'),
  (postAuthNotifications) => {
    const { clientBanner } = postAuthNotifications;
    return clientBanner || {};
  },
);

const selectHasUnreadNotifications = () => createSelector(
  selectHasUnreadSecureMessages(),
  selectHasUnreadLossDraftClaimEvents(),
  selectHasUnreadLossDraftMessages(),
  selectHasUnreadPostAuthNotifications(),
  selectHasUnreadDocUploadStatusNotifications(),
  (
    hasUnreadSecureMessages,
    hasUnreadLossDraftClaimEvents,
    hasUnreadLossDraftMessages,
    hasUnreadPostAuthNotifications,
    hasUnreadDocUploadStatusNotifications,
  ) => (
    hasUnreadLossDraftClaimEvents
    || hasUnreadPostAuthNotifications
    || hasUnreadSecureMessages
    || hasUnreadLossDraftMessages
    || hasUnreadDocUploadStatusNotifications
  ),
);

const selectHasUnreadPostAuthNotifications = () => createSelector(
  getProfileCurrentSetting(),
  makeSelectNotifications('postAuthNotifications'),
  makeSelectNotifications('notificationsModalAutoOpened'),
  ({ lastLoginDate }, { standardBanners = [] }, notificationsModalAutoOpened) => {
    if (standardBanners.length && !notificationsModalAutoOpened.length) {
      const ordered = standardBanners.slice().sort(
        (a, b) => moment(b.date, 'MMMM D, YYYY').isSame(moment(a.date, 'MMMM D, YYYY')) ?
          moment(b.lastUpdated).valueOf() - moment(a.lastUpdated).valueOf() :
          moment(b.date, 'MMMM DD, YYYY').valueOf() - moment(a.date, 'MMMM DD, YYYY').valueOf(),
      );
      if (moment.utc(lastLoginDate).isSame(moment(ordered[0].date, 'MMMM D, YYYY'), 'day')) {
        return moment.utc(lastLoginDate).isSameOrBefore(
          moment(ordered[0].lastUpdated),
        );
      }
      return moment.utc(lastLoginDate).isBefore(moment(ordered[0].date, 'MMMM D, YYYY'), 'day');
    }
    return false;
  },
);

const selectNotificationsModalFetchRequestsLoading = () => createSelector(
  makeSelectNotifications('loading'),
  makeSelectLossDraft('loading'),
  selectLossDraftRequestStatus('fetchMessages'),
  selectSecureMessagingRequestStatus('fetchMessages'),
  (
    isNotificationsFetchRequestLoading,
    isLossDraftFetchRequestLoading,
    lossDraftRequestStatusFetchMessages,
    secureMessagingRequestStatusFetchMessages,
  ) => isNotificationsFetchRequestLoading
    || isLossDraftFetchRequestLoading
    || lossDraftRequestStatusFetchMessages === 'loading'
    || secureMessagingRequestStatusFetchMessages === 'loading',
);

const selectPostAuthNotifications = () => createSelector(
  makeSelectNotifications('postAuthNotifications'),
  selectLossDraftMessageInquiryNotifications(),
  selectSecureMessagingInquiryNotifications(),
  selectLossDraftUnreadMessageNotifications(),
  selectSecureMessagingUnreadMessageNotifications(),
  selectIntl,
  selectDocUploadStatusNotifications(),
  (
    postAuthNotifications,
    lossDraftMessageInquiryNotifications,
    secureMessagingInquiryNotifications,
    lossDraftUnreadNotifications,
    secureMessagingUnreadNotifications,
    intl,
    docUploadStatusNotifications,
  ) => {
    const { standardBanners } = postAuthNotifications;
    const standardBannersToUse = standardBanners || [];
    const sortedNotifications =
    [
      ...lossDraftMessageInquiryNotifications,
      ...lossDraftUnreadNotifications,
      ...formatIntlNotifications({ intl, notifications: RELEASE_NOTIFICATIONS }),
      ...secureMessagingInquiryNotifications,
      ...secureMessagingUnreadNotifications,
      ...standardBannersToUse,
      ...docUploadStatusNotifications,
    ].sort((a, b) => moment(b.date, 'MMMM D, YYYY').isSame(moment(a.date, 'MMMM D, YYYY')) ?
      moment(b.lastUpdated).valueOf() - moment(a.lastUpdated).valueOf() :
      moment(b.date, 'MMMM D, YYYY').valueOf() - moment(a.date, 'MMMM D, YYYY').valueOf());
    return sortedNotifications;
  },
);

const selectPreAuthNotifications = () => createSelector(
  makeSelectNotifications('preAuthNotifications'),
  (preAuthNotifications) => preAuthNotifications,
);

const selectShouldNotificationsModalAutoOpen = () => createSelector(
  makeSelectMain('selectedLoan'),
  makeSelectMain('isInactivityModalOpen'),
  makeSelectNotifications('notificationsModalAutoOpened'),
  selectNotificationsModalFetchRequestsLoading(),
  selectHasUnreadNotifications(),
  selectIsNYCLanguagePreferenceModalRequired(),
  selectChatbotWindowStatus(),
  (
    selectedLoan,
    isInactivityModalOpen,
    notificationsModalAutoOpened,
    notificationsModalFetchRequestsLoading,
    hasUnreadNotifications,
    isNYCLanguagePreferenceModalRequired,
    chatbotWindowStatus,
  ) => (
    !isInactivityModalOpen &&
    !notificationsModalFetchRequestsLoading
    && !notificationsModalAutoOpened.includes(selectedLoan)
    && hasUnreadNotifications
    && !isNYCLanguagePreferenceModalRequired
    && chatbotWindowStatus !== 'open'
  ),
);

/**
 * Default selector used by Notifications
 */
const makeSelectNotifications = (prop) => createSelector(
  selectNotificationsDomain,
  (notificationsState) => notificationsState[prop],
);

export default makeSelectNotifications;
export {
  selectChatbotModalInteractionProps,
  selectClientBanner,
  selectNotificationsModalFetchRequestsLoading,
  selectPostAuthNotifications,
  selectPreAuthNotifications,
  selectShouldNotificationsModalAutoOpen,
};
